import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle, Eye, EyeOff, Loader2 } from 'lucide-react';
import Logo from '@/icons/Logo';

function extractTeamFromEmail(email: string): string {
  const domain = email.split('@')[1] || '';
  const name = domain.split('.')[0] || '';
  return name.length > 1 ? name.charAt(0).toUpperCase() + name.slice(1) : '';
}

const FullAssessmentSignup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [verifying, setVerifying] = useState(true);
  const [tokenError, setTokenError] = useState('');
  const [alreadyUsed, setAlreadyUsed] = useState(false);
  const [email, setEmail] = useState('');

  const [fullName, setFullName] = useState('');
  const [team, setTeam] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setTokenError('Missing signup token. Please use the link from your invitation email.');
        setVerifying(false);
        return;
      }
      const { data, error: fnErr } = await supabase.functions.invoke('complete-full-assessment-signup', {
        body: { token, verifyOnly: true },
      });
      if (fnErr || (data && (data as any).error)) {
        const errBody = (data as any) || {};
        setTokenError(errBody.error || fnErr?.message || 'Invalid or expired signup link.');
        setAlreadyUsed(!!errBody.alreadyUsed);
        if (errBody.email) setEmail(errBody.email);
        setVerifying(false);
        return;
      }
      const verifiedEmail = (data as any)?.email || '';
      setEmail(verifiedEmail);
      setTeam(extractTeamFromEmail(verifiedEmail));
      setVerifying(false);
    };
    verify();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('complete-full-assessment-signup', {
        body: { token, fullName, team, password },
      });
      const result = (data as any) || {};
      if (fnErr || result.error) {
        setError(result.error || fnErr?.message || 'Failed to create account');
        setSubmitting(false);
        return;
      }

      // Sign the user in immediately with the password they just set
      const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
      if (signInErr) {
        // Account was created — direct them to the login page
        setSuccess(true);
        setTimeout(() => navigate('/'), 2000);
        return;
      }
      setSuccess(true);
      setTimeout(() => navigate('/full-assessment'), 1500);
    } catch (err: any) {
      setError(err.message || 'Unexpected error');
      setSubmitting(false);
    }
  };

  if (verifying) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-muted/30">
        <div className="flex items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="text-lg text-foreground">Verifying your signup link...</span>
        </div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-2"><Logo className="h-10 w-10" /></div>
            <CardTitle className="text-2xl font-bold">
              {alreadyUsed ? 'Already Signed Up' : 'Link Unavailable'}
            </CardTitle>
            <CardDescription>{tokenError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">
              {alreadyUsed ? 'Go to Sign In' : 'Back to Home'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-2"><Logo className="h-10 w-10" /></div>
            <CardTitle className="text-2xl font-bold">You're All Set!</CardTitle>
            <CardDescription>Your account has been created. Redirecting...</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-2"><Logo className="h-10 w-10" /></div>
          <CardTitle className="text-2xl font-bold">Create Your Account</CardTitle>
          <CardDescription>
            Welcome to Maturity Compass. Set up your account to access the full assessment.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={email} disabled />
            </div>

            <div className="space-y-2">
              <Label htmlFor="signup-name">Full Name</Label>
              <Input
                id="signup-name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Your full name"
                required
                disabled={submitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="signup-team">Team / Organization</Label>
              <Input
                id="signup-team"
                value={team}
                onChange={(e) => setTeam(e.target.value)}
                placeholder="Your team or organization"
                disabled={submitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="signup-password">Password</Label>
              <div className="relative">
                <Input
                  id="signup-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password"
                  required
                  disabled={submitting}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">Must be at least 8 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="signup-confirm">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="signup-confirm"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  disabled={submitting}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {submitting ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default FullAssessmentSignup;
